import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, showToast, setLotSize } from "../../store/uiSlice";
import {
  updatePosition,
  setBalance,
  setPortfolio,
  addHistoryOrder,
} from "../../store/tradeSlice";
import { fmtP } from "../../utils/formatters";
import { LOT_LABELS, getLotSize } from "../../utils/constants";
import { getForexUSDPrice, getNotionalUSD } from "../../utils/forexUtils";
import api from "../../services/api";

const getSpread = (price, category) => {
  if (!price || price === 0) return 0;
  if (category === "forex") return 0.0002;
  if (category === "crypto") return price * 0.001;
  if (category === "metal") return price * 0.002;
  return price * 0.002;
};

const getLeverageMultiplier = (leverageStr, category) => {
  if (category === "crypto") return 25;
  if (category === "stock") return 4;
  if (!leverageStr) return 50;
  const parts = String(leverageStr).split(":");
  return parseInt(parts[1]) || parseInt(parts[0]) || 50;
};

const getLeverageDisplay = (category, userLeverage) => {
  if (category === "crypto") return "1:25";
  if (category === "stock") return "1:4";
  return userLeverage || "1:100";
};

const getMarginRate = (category, userLeverage) => {
  const multiplier = getLeverageMultiplier(userLeverage, category);
  return (100 / multiplier).toFixed(1);
};

const BuyModal = ({ instrument }) => {
  const dispatch = useDispatch();
  const { buyModal } = useSelector((s) => s.ui.modals);
  const { lotSize } = useSelector((s) => s.ui);
  const { marketData, balance, portfolio } = useSelector((s) => s.trade);
  const user = useSelector((s) => s.auth.user);
  const theme = useSelector((s) => s.ui.theme);
  const isLight = theme === "light";

  const [orderType, setOrderType] = useState("market");
  const [quantity, setQuantity] = useState(lotSize || 0.01);
  const [qtyRaw, setQtyRaw] = useState(String(lotSize || 0.01));
  const [limitPrice, setLimitPrice] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimation, setEstimation] = useState({
    totalValue: 0,
    requiredMargin: 0,
    percentOfBalance: 0,
    estimatedProfit: null,
    estimatedLoss: null,
  });

  const userLeverage = user?.leverage || "1:100";
  const leverageMultiplier = getLeverageMultiplier(
    userLeverage,
    instrument?.cat,
  );
  const leverageDisplay = getLeverageDisplay(instrument?.cat, userLeverage);
  const marginRatePct = getMarginRate(instrument?.cat, userLeverage);

  const existingPosition = instrument ? portfolio?.[instrument.id] : null;
  const hasShortPosition = existingPosition && existingPosition.qty < -0.001;

  const buyBtnBg = isLight
    ? "linear-gradient(to right, #059669, #047857)"
    : "rgba(5, 150, 105, 0.2)";
  const buyBtnBorder = isLight ? "none" : "1px solid rgba(5, 150, 105, 0.35)";
  const buyBtnShadow = isLight ? "0 4px 16px rgba(5,150,105,0.3)" : "none";

  useEffect(() => {
    if (buyModal && instrument) {
      setOrderType("market");
      setQuantity(lotSize || 0.01);
      setQtyRaw(String(lotSize || 0.01));
      setLimitPrice("");
      setTakeProfit("");
      setStopLoss("");
    }
  }, [buyModal, instrument]);

  useEffect(() => {
    updateEstimation();
  }, [
    quantity,
    orderType,
    limitPrice,
    takeProfit,
    stopLoss,
    instrument,
    marketData,
    balance,
  ]);

  const updateEstimation = () => {
    if (!instrument) return;
    const data = marketData[instrument.id];
    const marketPrice = data?.price || 0;
    const spread = getSpread(marketPrice, instrument.cat);
    const ls = getLotSize(instrument.cat, instrument.id);
    const askPrice = marketPrice + spread / 2;
    let execPrice =
      orderType === "market" ? askPrice : parseFloat(limitPrice) || 0;
    if (orderType !== "market" && execPrice <= 0) {
      setEstimation({
        totalValue: 0,
        requiredMargin: 0,
        percentOfBalance: 0,
        estimatedProfit: null,
        estimatedLoss: null,
      });
      return;
    }
    const totalValue = getNotionalUSD(
      instrument.id,
      execPrice,
      quantity,
      instrument.cat,
      ls,
      marketData,
    );
    const requiredMargin = totalValue / leverageMultiplier;
    const percentOfBalance = balance > 0 ? (requiredMargin / balance) * 100 : 0;
    let estimatedProfit = null;
    const tp = parseFloat(takeProfit);
    if (takeProfit && !isNaN(tp) && tp > execPrice) {
      const usdEntry = getForexUSDPrice(
        instrument.id,
        execPrice,
        instrument.cat,
        marketData,
      );
      const usdTp = getForexUSDPrice(
        instrument.id,
        tp,
        instrument.cat,
        marketData,
      );
      estimatedProfit = (usdTp - usdEntry) * quantity * ls;
    }
    let estimatedLoss = null;
    const sl = parseFloat(stopLoss);
    if (stopLoss && !isNaN(sl) && sl > 0 && sl < execPrice) {
      const usdEntry = getForexUSDPrice(
        instrument.id,
        execPrice,
        instrument.cat,
        marketData,
      );
      const usdSl = getForexUSDPrice(
        instrument.id,
        sl,
        instrument.cat,
        marketData,
      );
      estimatedLoss = (usdSl - usdEntry) * quantity * ls;
    }
    setEstimation({
      totalValue,
      requiredMargin,
      percentOfBalance,
      estimatedProfit,
      estimatedLoss,
    });
  };

  const applyQty = (raw) => {
    setQtyRaw(raw);
    let v = parseFloat(raw);
    if (isNaN(v) || v < 0.01) return;
    if (v > 100) v = 100;
    v = parseFloat(v.toFixed(2));
    setQuantity(v);
    dispatch(setLotSize(v));
  };
  const blurQty = () => {
    let v = parseFloat(qtyRaw);
    if (isNaN(v) || v < 0.01) v = 0.01;
    if (v > 100) v = 100;
    v = parseFloat(v.toFixed(2));
    setQuantity(v);
    setQtyRaw(String(v));
    dispatch(setLotSize(v));
  };
  const stepQty = (delta) => {
    let v = quantity + delta;
    if (v < 0.01) v = 0.01;
    if (v > 100) v = 100;
    v = parseFloat(v.toFixed(2));
    setQuantity(v);
    setQtyRaw(String(v));
    dispatch(setLotSize(v));
  };

  const refreshUserData = async () => {
    try {
      const [balRes, portRes] = await Promise.all([
        api.get("/user/balance"),
        api.get("/user/portfolio"),
      ]);
      dispatch(setBalance(parseFloat(balRes.data.balance) || 0));
      dispatch(setPortfolio(portRes.data));
    } catch (e) {}
  };

  const handleSubmit = async () => {
    if (!instrument || isSubmitting) return;
    if (!user?.questionnaireComplete) {
      dispatch(
        showToast({
          message:
            "Please complete the trading questionnaire before placing trades",
          type: "error",
          duration: 5000,
        }),
      );
      window.dispatchEvent(new CustomEvent("open-questionnaire"));
      return;
    }
    const data = marketData[instrument.id];
    if (!data?.price) {
      dispatch(showToast({ message: "Price not available", type: "error" }));
      return;
    }
    const spread = getSpread(data.price, instrument.cat);
    let execPrice = data.price + spread / 2;
    if (orderType !== "market") {
      if (!limitPrice || parseFloat(limitPrice) <= 0) {
        dispatch(
          showToast({
            message: "Please enter a valid limit/stop price",
            type: "error",
          }),
        );
        return;
      }
      execPrice = parseFloat(limitPrice);
    }
    if (orderType === "market" && !hasShortPosition) {
      if (estimation.requiredMargin > balance) {
        dispatch(
          showToast({
            message: `Insufficient margin. Need $${estimation.requiredMargin.toFixed(2)} (Leverage: ${leverageDisplay})`,
            type: "error",
          }),
        );
        return;
      }
    }
    setIsSubmitting(true);
    const payload = {
      instrumentId: instrument.id,
      category: instrument.cat,
      quantity,
      orderType,
    };
    if (orderType !== "market") payload.price = execPrice;
    const tp = parseFloat(takeProfit),
      sl = parseFloat(stopLoss);
    if (takeProfit && !isNaN(tp) && tp > 0) payload.takeProfit = tp;
    if (stopLoss && !isNaN(sl) && sl > 0) payload.stopLoss = sl;
    try {
      const response = await api.post("/trade/buy", payload);
      if (response.data.success) {
        if (response.data.newBalance !== undefined)
          dispatch(setBalance(parseFloat(response.data.newBalance)));
        if (response.data.position)
          dispatch(
            updatePosition({
              instrumentId: instrument.id,
              position: response.data.position,
            }),
          );
        if (response.data.trade && response.data.trade.status === "closed")
          dispatch(addHistoryOrder(response.data.trade));
        const isClosingShort = hasShortPosition;
        const pnlText =
          isClosingShort && response.data.pnl !== undefined
            ? ` · P&L: ${response.data.pnl >= 0 ? "+" : ""}$${parseFloat(response.data.pnl).toFixed(2)}`
            : ` · Margin: $${estimation.requiredMargin.toFixed(2)}`;
        dispatch(
          showToast({
            message: `BUY ${quantity} lot(s) ${instrument.id} @ ${fmtP(execPrice, instrument.cat)}${pnlText}`,
            type: "buy",
          }),
        );
        dispatch(closeModal("buyModal"));
        await refreshUserData();
      }
    } catch (error) {
      dispatch(
        showToast({
          message:
            error.response?.data?.message || "Failed to execute buy order",
          type: "error",
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!instrument) return null;

  const data = marketData[instrument.id];
  const marketPrice = data?.price || 0;
  const spread = getSpread(marketPrice, instrument.cat);
  const askPrice = marketPrice + spread / 2;
  const riskLevel = estimation.percentOfBalance;
  const getRiskColor = () => {
    if (riskLevel < 5) return "var(--color-green)";
    if (riskLevel < 15) return "var(--color-yellow)";
    if (riskLevel < 30) return "var(--color-text)";
    return "var(--color-red)";
  };
  const getRiskLabel = () => {
    if (riskLevel < 5) return "Low";
    if (riskLevel < 15) return "Medium";
    if (riskLevel < 30) return "High";
    return "Critical";
  };
  const fmtEst = (val) => {
    if (val === null || val === undefined) return "—";
    const abs = Math.abs(val);
    const sign = val >= 0 ? "+" : "-";
    return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getLotLabel = () => {
    const ls = getLotSize(instrument.cat, instrument.id);
    const labels = {
      forex: `1 lot = 100,000 units`,
      crypto: `1 lot = 12 coin`,
      metal: instrument.id === "XAGUSD" ? `1 lot = 1,000 oz` : `1 lot = 100 oz`,
      stock: `1 lot = 1 share`,
      index: `1 lot = 100 unit`,
      commodity: `1 lot = 1,000 units`,
    };
    return labels[instrument.cat] || `${ls} units per lot`;
  };

  const inputStyle = {
    background: "var(--color-bgInput)",
    border: "1px solid var(--color-borderMedium)",
    color: "var(--color-text)",
  };

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 14px; height: 14px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: var(--color-bgSurfaceLight); border-radius: 12px; margin: 8px 2px; border: 1px solid var(--color-borderMedium); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--color-orange), var(--color-orangeDark)); border-radius: 12px; border: 3px solid var(--color-bgSurfaceLight); min-height: 50px; cursor: pointer; transition: all 0.2s ease; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, var(--color-orangeLight), var(--color-orange)); border: 2px solid var(--color-bgSurfaceLight); }
        .custom-scrollbar { scrollbar-width: auto; scrollbar-color: var(--color-orange) var(--color-bgSurfaceLight); }
      `}</style>

      <div
        className={`fixed inset-0 z-[800] flex items-center justify-center p-0 sm:p-4 transition-all duration-300 ${buyModal ? "visible" : "invisible"}`}
      >
        <div
          className={`absolute inset-0 backdrop-blur-md transition-opacity duration-300 ${buyModal ? "opacity-100" : "opacity-0"}`}
          style={{ background: "var(--color-bgOverlay)" }}
          onClick={() => dispatch(closeModal("buyModal"))}
        />
        <div
          className={`relative sm:rounded-2xl w-full max-w-[520px] h-full sm:max-h-[90vh] overflow-y-auto shadow-2xl transition-all duration-300 custom-scrollbar ${buyModal ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
          style={{
            background: "var(--color-bgSurface)",
            border: "1px solid var(--color-border)",
          }}
        >
          {/* Header */}
          <div
            className="sticky top-0 z-10 px-4 sm:px-5 py-4 sm:py-4 flex items-center justify-between"
            style={{
              background: "var(--color-bgSurface)",
              borderBottom: "1px solid var(--color-borderMedium)",
            }}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div
                className="w-10 h-10 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center text-lg sm:text-lg flex-shrink-0"
                style={{
                  background: "var(--color-greenBg)",
                  border: "1px solid var(--color-greenBorder)",
                  color: "var(--color-green)",
                }}
              >
                <i className="fas fa-arrow-trend-up" />
              </div>
              <div className="min-w-0">
                <div
                  className="font-syne text-lg sm:text-lg font-extrabold truncate"
                  style={{ color: "var(--color-text)" }}
                >
                  {hasShortPosition ? "BUY" : "BUY / LONG"} — {instrument.id}
                </div>
                <div
                  className="text-sm sm:text-sm font-mono truncate"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {hasShortPosition
                    ? "Closing short position"
                    : orderType === "market"
                      ? "Market Order"
                      : orderType === "limit"
                        ? "Limit Order"
                        : "Stop Order"}{" "}
                  · {leverageDisplay} · {marginRatePct}% margin
                </div>
              </div>
            </div>
            <button
              onClick={() => dispatch(closeModal("buyModal"))}
              className="w-10 h-10 sm:w-8 sm:h-8 rounded-md cursor-pointer flex items-center justify-center transition-all active:scale-90 flex-shrink-0"
              style={{
                background: "var(--color-bgCard)",
                border: "1px solid var(--color-borderMedium)",
                color: "var(--color-textSecondary)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-red)";
                e.currentTarget.style.color = "var(--color-red)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-borderMedium)";
                e.currentTarget.style.color = "var(--color-textSecondary)";
              }}
            >
              <i className="fas fa-xmark text-lg" />
            </button>
          </div>

          {/* Questionnaire warning */}
          {!user?.questionnaireComplete && (
            <div
              className="mx-4 sm:mx-5 mt-3 sm:mt-4 p-3.5 rounded-xl flex gap-2.5"
              style={{
                background: "var(--color-orangeBg)",
                border: "1px solid var(--color-orangeBorder)",
              }}
            >
              <i
                className="fas fa-clipboard-list text-lg mt-0.5 flex-shrink-0"
                style={{ color: "var(--color-text)" }}
              />
              <div
                className="text-sm font-mono leading-relaxed"
                style={{ color: "var(--color-textSecondary)" }}
              >
                <b style={{ color: "var(--color-text)" }}>
                  Questionnaire Required:
                </b>{" "}
                You must complete the trading questionnaire before placing
                trades.
                <button
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent("open-questionnaire"))
                  }
                  className="block mt-1.5 font-bold hover:underline text-sm"
                  style={{ color: "var(--color-text)" }}
                >
                  Complete Questionnaire →
                </button>
              </div>
            </div>
          )}

          {/* Stats Row */}
          <div
            className="grid grid-cols-3 gap-2 sm:gap-2 p-4 sm:p-4"
            style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
          >
            {[
              {
                label: "Ask",
                value: fmtP(askPrice, instrument.cat),
                color: "var(--color-green)",
                sub: "Buy price",
              },
              {
                label: "Balance",
                value: `$${parseFloat(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                color: "var(--color-text)",
                sub: "Available",
              },
              {
                label: "Leverage",
                value: leverageDisplay,
                color: "var(--color-purpleLight)",
                sub: `${marginRatePct}% margin`,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl p-3 text-center"
                style={{
                  background: "var(--color-bgCard)",
                  border: "1px solid var(--color-borderMedium)",
                }}
              >
                <div
                  className="text-xs sm:text-xs font-mono tracking-[0.1em] uppercase mb-1.5"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {item.label}
                </div>
                <div
                  className="font-syne text-base sm:text-lg font-extrabold"
                  style={{ color: item.color }}
                >
                  {item.value}
                </div>
                <div
                  className="text-[11px] sm:text-[10px] font-mono mt-1"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {item.sub}
                </div>
              </div>
            ))}
          </div>

          {/* Has Short Position Warning */}
          {hasShortPosition && (
            <div
              className="mx-4 sm:mx-5 mt-3 sm:mt-4 p-3.5 rounded-xl flex gap-2.5"
              style={{
                background: "var(--color-greenBg)",
                border: "1px solid var(--color-greenBorder)",
              }}
            >
              <i
                className="fas fa-info-circle text-lg mt-0.5 flex-shrink-0"
                style={{ color: "var(--color-green)" }}
              />
              <div
                className="text-sm font-mono leading-relaxed"
                style={{ color: "var(--color-greenLight)" }}
              >
                You have a <b>SHORT</b> position on {instrument.id}.<br />
                Buying will close it and realise your P&amp;L.
              </div>
            </div>
          )}

          {/* Order Type */}
          <div
            className="px-4 sm:px-5 pt-4 sm:pt-4 pb-3"
            style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
          >
            <div
              className="text-xs sm:text-xs font-mono tracking-[0.1em] uppercase mb-3 flex items-center gap-1.5"
              style={{ color: "var(--color-textSecondary)" }}
            >
              <i
                className="fas fa-list-check text-sm"
                style={{ color: "var(--color-text)" }}
              />{" "}
              Order Type
            </div>
            <div className="grid grid-cols-3 gap-2 sm:gap-2">
              {[
                {
                  type: "market",
                  label: "MARKET",
                  icon: "fa-bolt",
                  desc: "Execute now",
                },
                {
                  type: "limit",
                  label: "BUY LIMIT",
                  icon: "fa-arrow-trend-down",
                  desc: "Buy when drops",
                },
                {
                  type: "stop",
                  label: "BUY STOP",
                  icon: "fa-arrow-trend-up",
                  desc: "Buy on breakout",
                },
              ].map((option) => (
                <button
                  key={option.type}
                  onClick={() => setOrderType(option.type)}
                  className="py-3 rounded-lg text-center transition-all border active:scale-95"
                  style={{
                    background:
                      orderType === option.type
                        ? "var(--color-greenBg)"
                        : "var(--color-bgCard)",
                    borderColor:
                      orderType === option.type
                        ? "var(--color-greenBorder)"
                        : "var(--color-borderMedium)",
                    color:
                      orderType === option.type
                        ? "var(--color-green)"
                        : "var(--color-textSecondary)",
                    minHeight: "60px",
                  }}
                >
                  <div className="text-lg mb-1">
                    <i className={`fas ${option.icon}`} />
                  </div>
                  <div className="text-xs sm:text-xs font-syne font-bold">
                    {option.label}
                  </div>
                  <div
                    className="text-[11px] sm:text-[10px] mt-0.5"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    {option.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Order Parameters */}
          <div
            className="px-4 sm:px-5 py-4 sm:py-4"
            style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
          >
            <div
              className="text-xs sm:text-xs font-mono tracking-[0.1em] uppercase mb-3.5 flex items-center gap-1.5"
              style={{ color: "var(--color-textSecondary)" }}
            >
              <i
                className="fas fa-sliders text-sm"
                style={{ color: "var(--color-text)" }}
              />{" "}
              Order Parameters
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-3 mb-3.5">
              <div>
                <div
                  className="text-xs sm:text-xs font-mono mb-2.5 flex items-center gap-1.5"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  <i
                    className="fas fa-layer-group text-xs"
                    style={{ color: "var(--color-text)" }}
                  />{" "}
                  Quantity (Lots)
                </div>
                <div
                  className="flex items-center justify-center rounded-lg overflow-hidden"
                  style={{
                    border: "1px solid var(--color-borderMedium)",
                    background: "var(--color-bgInput)",
                  }}
                >
                  <button
                    onClick={() => stepQty(-0.01)}
                    className="w-12 h-12 sm:w-10 sm:h-10 cursor-pointer flex items-center justify-center active:scale-90 transition-all font-bold text-2xl sm:text-xl"
                    style={{
                      background: "var(--color-bgSurfaceLight)",
                      borderRight: "1px solid var(--color-borderMedium)",
                      color: "var(--color-text)",
                    }}
                  >
                    −
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={qtyRaw}
                    onChange={(e) => applyQty(e.target.value)}
                    onBlur={blurQty}
                    className="flex-1 bg-transparent border-none text-center font-mono text-xl sm:text-xl font-bold py-3 outline-none"
                    style={{ color: "var(--color-text)" }}
                  />
                  <button
                    onClick={() => stepQty(0.01)}
                    className="w-12 h-12 sm:w-10 sm:h-10 cursor-pointer flex items-center justify-center active:scale-90 transition-all font-bold text-2xl sm:text-xl"
                    style={{
                      background: "var(--color-bgSurfaceLight)",
                      borderLeft: "1px solid var(--color-borderMedium)",
                      color: "var(--color-text)",
                    }}
                  >
                    +
                  </button>
                </div>
                <div
                  className="text-xs sm:text-xs font-mono mt-2.5 text-center"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {getLotLabel()}
                </div>
              </div>
              {orderType !== "market" && (
                <div>
                  <div
                    className="text-xs sm:text-xs font-mono mb-2.5 flex items-center gap-1.5"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    <i
                      className="fas fa-tag text-xs"
                      style={{ color: "var(--color-text)" }}
                    />{" "}
                    Target Price
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(e.target.value)}
                    placeholder={
                      orderType === "limit" ? "Below market" : "Above market"
                    }
                    className="w-full rounded-lg px-3 py-3.5 sm:py-2.5 font-mono text-base outline-none transition-all"
                    style={inputStyle}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "var(--color-orange)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "var(--color-borderMedium)")
                    }
                  />
                  <div
                    className="text-xs sm:text-xs font-mono mt-1.5"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Ask: {fmtP(askPrice, instrument.cat)}
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-3">
              <div>
                <div
                  className="text-xs sm:text-xs font-mono mb-2"
                  style={{ color: "var(--color-green)" }}
                >
                  <i className="fas fa-bullseye text-xs mr-1" /> Take Profit
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  placeholder={`> ${fmtP(askPrice, instrument.cat)}`}
                  className="w-full rounded-lg px-3 py-3.5 sm:py-2.5 font-mono text-base outline-none transition-all"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-green)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
              <div>
                <div
                  className="text-xs sm:text-xs font-mono mb-2"
                  style={{ color: "var(--color-red)" }}
                >
                  <i className="fas fa-shield-halved text-xs mr-1" /> Stop Loss
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  placeholder={`< ${fmtP(askPrice, instrument.cat)}`}
                  className="w-full rounded-lg px-3 py-3.5 sm:py-2.5 font-mono text-base outline-none transition-all"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-red)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
            </div>
          </div>

          {/* Order Estimation */}
          <div
            className="mx-4 sm:mx-5 my-4 sm:my-4 rounded-xl overflow-hidden"
            style={{ border: "1px solid var(--color-borderMedium)" }}
          >
            <div
              className="flex items-center justify-between px-3.5 py-3 border-b"
              style={{
                background: "var(--color-bgCard)",
                borderColor: "var(--color-borderMedium)",
              }}
            >
              <span
                className="text-sm sm:text-sm font-syne font-bold flex items-center gap-1.5"
                style={{ color: "var(--color-text)" }}
              >
                <i className="fas fa-calculator text-sm" /> Order Estimation
              </span>
              <span
                className="text-xs sm:text-xs px-2.5 py-1 rounded font-mono font-bold"
                style={{
                  background: "var(--color-greenBg)",
                  color: "var(--color-green)",
                  border: "1px solid var(--color-greenBorder)",
                }}
              >
                {orderType.toUpperCase()}
              </span>
            </div>
            <div
              className="divide-y"
              style={{
                background: "var(--color-bgCard)",
                borderColor: "var(--color-borderLight)",
              }}
            >
              {[
                {
                  label: "Total Position Value",
                  icon: "fa-coins",
                  value: `$${estimation.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  color: "var(--color-textSecondary)",
                },
                {
                  label: `Required Margin (${leverageDisplay})`,
                  icon: "fa-percent",
                  value: `$${estimation.requiredMargin.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  color: "var(--color-text)",
                  highlight: true,
                },
                ...(estimation.estimatedProfit !== null
                  ? [
                      {
                        label: "Est. Profit (TP Hit)",
                        icon: "fa-bullseye",
                        value: fmtEst(estimation.estimatedProfit),
                        color: "var(--color-green)",
                        highlight: true,
                      },
                    ]
                  : []),
                ...(estimation.estimatedLoss !== null
                  ? [
                      {
                        label: "Est. Loss (SL Hit)",
                        icon: "fa-shield-halved",
                        value: fmtEst(estimation.estimatedLoss),
                        color: "var(--color-red)",
                        highlight: true,
                      },
                    ]
                  : []),
                {
                  label: "% of Balance",
                  icon: "fa-chart-pie",
                  value: `${estimation.percentOfBalance.toFixed(2)}%`,
                  color: getRiskColor(),
                },
              ].map((row, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center px-3.5 py-3"
                  style={{
                    background: row.highlight
                      ? "var(--color-orangeBg)"
                      : "transparent",
                  }}
                >
                  <span
                    className="text-xs sm:text-xs font-bold flex items-center gap-1.5"
                    style={{ color: row.color }}
                  >
                    <i className={`fas ${row.icon} text-xs`} /> {row.label}
                  </span>
                  <span
                    className="font-mono text-sm sm:text-sm font-bold"
                    style={{ color: row.color }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Bar */}
          <div className="px-4 sm:px-5 pb-4 sm:pb-4">
            <div className="flex justify-between items-center mb-2">
              <span
                className="text-xs sm:text-xs font-mono"
                style={{ color: "var(--color-textSecondary)" }}
              >
                Position Risk
              </span>
              <span
                className="text-xs sm:text-xs font-mono font-bold"
                style={{ color: getRiskColor() }}
              >
                {getRiskLabel()}
              </span>
            </div>
            <div
              className="h-2.5 rounded-full overflow-hidden"
              style={{ background: "var(--color-borderMedium)" }}
            >
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(riskLevel * 2, 100)}%`,
                  background: getRiskColor(),
                }}
              />
            </div>
            <div className="flex justify-between mt-2">
              {["Low", "Med", "High", "Critical"].map((l, i) => (
                <span
                  key={l}
                  className="text-[11px] sm:text-[10px] font-bold"
                  style={{
                    color:
                      i === 0
                        ? "var(--color-green)"
                        : i === 1
                          ? "var(--color-yellow)"
                          : i === 2
                            ? "var(--color-text)"
                            : "var(--color-red)",
                  }}
                >
                  {l}
                </span>
              ))}
            </div>
          </div>

          {/* Insufficient Margin */}
          {estimation.requiredMargin > balance && !hasShortPosition && (
            <div
              className="mx-4 sm:mx-5 mb-3.5 p-3.5 rounded-xl flex gap-2.5"
              style={{
                background: "var(--color-redBg)",
                border: "1px solid var(--color-redBorder)",
              }}
            >
              <i
                className="fas fa-triangle-exclamation text-lg mt-0.5 flex-shrink-0"
                style={{ color: "var(--color-red)" }}
              />
              <div
                className="text-sm font-mono leading-relaxed"
                style={{ color: "var(--color-redLight)" }}
              >
                Insufficient margin. Need{" "}
                <b>${estimation.requiredMargin.toFixed(2)}</b> but balance is{" "}
                <b>${parseFloat(balance).toFixed(2)}</b>.
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            className="flex gap-3 p-4 sm:p-5 pt-2"
            style={{
              borderTop: "1px solid var(--color-borderMedium)",
              background: "var(--color-bgCard)",
            }}
          >
            <button
              onClick={() => dispatch(closeModal("buyModal"))}
              className="flex-1 py-4 rounded-full font-syne font-bold text-sm transition-all active:scale-95"
              style={{
                background: "var(--color-bgCard)",
                border: "1px solid var(--color-borderHover)",
                color: "var(--color-textSecondary)",
                minHeight: "56px",
              }}
            >
              <i className="fas fa-xmark mr-1.5" /> Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                (estimation.requiredMargin > balance && !hasShortPosition)
              }
              className="flex-1 py-4 rounded-full font-syne font-bold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: buyBtnBg,
                border: buyBtnBorder,
                color: "#ffffff",
                minHeight: "56px",
                boxShadow: buyBtnShadow,
              }}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing…
                </>
              ) : (
                <>
                  <i className="fas fa-arrow-trend-up mr-1.5" />
                  {hasShortPosition ? "BUY" : "Confirm BUY"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BuyModal;